import { useRef } from "react";
import { Link } from "wouter";
import { PublicLayout } from "@/components/public/public-layout";
import { HeroParallaxBand } from "@/components/public/hero-parallax-band";
import { InvestorPortalBand } from "@/components/public/investor-portal-band";
import {
  Container,
  Section,
  SectionIntro,
  StatGrid,
  TextLink,
} from "@/components/public/institutional-ui";
import { ContactInquiryForm } from "@/components/public/contact-inquiry-form";
import { firmStats, pillars, services, transactions, insights } from "@/lib/site-content";
import { FadeIn } from "@/components/ui/scroll-animations";
import { ArrowRight } from "lucide-react";

export default function HomePage() {
  const contactRef = useRef<HTMLDivElement>(null);

  return (
    <PublicLayout>
      <HeroParallaxBand className="pb-24 md:pb-32 lg:pb-40">
        <Container className="w-full relative">
          <div className="relative max-w-5xl pt-8 md:pt-16">
            <FadeIn>
              <p className="gc-eyebrow text-foreground/75 mb-8 md:mb-10">Godman Capital</p>
              <h1 className="gc-heading-hero text-balance text-foreground drop-shadow-[0_1px_12px_rgba(255,255,255,0.9)]">
                Structured Capital for India&apos;s Growth Companies
              </h1>
              <p className="mt-8 md:mt-10 text-lg md:text-xl text-foreground/85 max-w-2xl leading-relaxed">
                Helping companies raise debt, private credit, structured finance and strategic
                capital.
              </p>
              <div className="mt-12 md:mt-14 flex flex-wrap gap-4">
                <Link href="/contact">
                  <span className="gc-btn-primary cursor-pointer inline-flex">
                    Raise Capital
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </span>
                </Link>
                <button
                  type="button"
                  className="gc-btn-outline inline-flex"
                  onClick={() =>
                    contactRef.current?.scrollIntoView({ behavior: "smooth" })
                  }
                >
                  Speak With Us
                </button>
              </div>
            </FadeIn>
          </div>
        </Container>
      </HeroParallaxBand>

      <Section variant="muted" className="!py-0">
        <Container className="!px-0 sm:!px-0 lg:!px-0 max-w-none">
          <StatGrid stats={firmStats} />
        </Container>
      </Section>

      <InvestorPortalBand />

      <Section>
        <Container>
          <SectionIntro
            eyebrow="The Godman edge"
            title="Institutional discipline. Entrepreneurial execution."
            description="We draw on deep experience in private credit and structured finance to deliver certainty for borrowers and allocators."
          />
          <div className="grid md:grid-cols-3 gap-px bg-border border border-border">
            {pillars.map((item, i) => (
              <FadeIn key={item.title} delay={i * 0.08}>
                <div className="bg-background p-8 md:p-10 lg:p-14 h-full">
                  <h3 className="text-lg font-medium mb-4 tracking-tight">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </Container>
      </Section>

      <Section variant="muted" id="what-we-do">
        <Container>
          <SectionIntro
            eyebrow="What we do"
            title="Capital solutions across the stack"
            description="Six institutional capabilities — no generic templates, no financial clichés."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
            {services.map((s, i) => (
              <FadeIn key={s.title} delay={i * 0.04}>
                <Link href="/what-we-do">
                  <article className="gc-strategy-card group cursor-pointer h-full">
                    <h3 className="text-xl font-medium mb-4 group-hover:opacity-70 transition-opacity">
                      {s.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {s.description}
                    </p>
                  </article>
                </Link>
              </FadeIn>
            ))}
          </div>
          <div className="mt-14">
            <TextLink href="/what-we-do">All capabilities</TextLink>
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionIntro
            eyebrow="Selected transactions"
            title="Transaction credibility"
            description="Representative mandates across renewable energy, education, hospitality, and logistics."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
            {transactions.map((tx, i) => (
              <FadeIn key={tx.label} delay={i * 0.05}>
                <div className="bg-background p-8 md:p-12">
                  <p className="gc-stat-value">{tx.amount}</p>
                  <p className="mt-4 text-sm text-muted-foreground leading-snug">{tx.label}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <div className="mt-14">
            <TextLink href="/transactions">View all transactions</TextLink>
          </div>
        </Container>
      </Section>

      <Section variant="muted">
        <Container>
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-28">
            <FadeIn>
              <SectionIntro
                eyebrow="About"
                title="Built for control, not participation."
                description="Godman Capital is an India-focused private credit platform. We structure capital with discipline on downside protection and long-term alignment."
              />
              <div className="-mt-6">
                <TextLink href="/about">Our story & leadership</TextLink>
              </div>
            </FadeIn>
            <FadeIn delay={0.1}>
              <div className="space-y-10">
                <div>
                  <p className="gc-eyebrow text-muted-foreground mb-3">Vision</p>
                  <p className="text-muted-foreground leading-relaxed italic">
                    To set the standard for boutique investment banking and private credit
                    advisory excellence in India.
                  </p>
                </div>
                <div>
                  <p className="gc-eyebrow text-muted-foreground mb-3">Positioning</p>
                  <p className="text-muted-foreground leading-relaxed">
                    Serious capital for founders, CFOs, and allocators who require structure,
                    speed, and confidentiality.
                  </p>
                </div>
              </div>
            </FadeIn>
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionIntro
            eyebrow="Insights"
            title="News & perspectives"
            description="Market insights, debt market reports, private credit trends, and founder financing guides."
          />
          <div className="border-t border-border">
            {insights.map((item, i) => (
              <FadeIn key={item.title} delay={i * 0.05}>
                <Link href="/insights">
                  <article className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 py-9 md:py-10 border-b border-border hover:opacity-70 transition-opacity cursor-pointer">
                    <div>
                      <p className="gc-eyebrow text-muted-foreground mb-2">{item.category}</p>
                      <h3 className="text-lg md:text-xl font-medium max-w-2xl">{item.title}</h3>
                    </div>
                    <span className="text-sm text-muted-foreground shrink-0">{item.date}</span>
                  </article>
                </Link>
              </FadeIn>
            ))}
          </div>
          <div className="mt-12">
            <TextLink href="/insights">All insights</TextLink>
          </div>
        </Container>
      </Section>

      <Section variant="muted" id="contact">
        <Container>
          <div ref={contactRef} className="grid lg:grid-cols-2 gap-16 lg:gap-28">
            <FadeIn>
              <SectionIntro
                eyebrow="Contact"
                title="Start a conversation"
                description="For capital mandates and advisory — reach our team directly."
              />
              <div className="space-y-2 text-sm -mt-6">
                <a
                  href="mailto:support@godmancapital.in"
                  className="hover:opacity-60 transition-opacity"
                >
                  support@godmancapital.in
                </a>
                <p className="text-muted-foreground">
                  11th Floor, Raheja Tower, MG Road, Bangalore – 560001
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={0.1}>
              <div className="border border-border p-8 md:p-10 bg-[hsl(var(--surface))]">
                <ContactInquiryForm source="home-page" />
              </div>
            </FadeIn>
          </div>
        </Container>
      </Section>
    </PublicLayout>
  );
}
